/**
 * Cartões do Kaizen (5.18): FSRS (ts-fsrs v5), itens aprendidos e níveis, fila, novos por dia,
 * kanji antes da palavra, irmãos, difíceis (leech) e distratores do lado Escrever. Funções puras.
 */
import { fsrs, generatorParameters, Rating, State, type Card as FsrsCard, type Grade, type StepUnit } from 'ts-fsrs';
import { addDays, type ISODate } from '../time';
import type { CardState, CardTemplate, NoteType } from '../types';

// ─── Tipos de item e lados (5.18.2) ────────────────────────────────────────

export const NOTE_TEMPLATES: Record<NoteType, [CardTemplate, CardTemplate]> = {
  kana: ['sound', 'write'],
  kanji: ['meaning', 'reading'],
  vocab_ja: ['read', 'recall'],
  vocab: ['understand', 'say'],
};

export const TEMPLATE_LABEL: Record<CardTemplate, string> = {
  sound: 'Som',
  write: 'Escrever',
  meaning: 'Significado',
  reading: 'Leitura',
  read: 'Ler',
  recall: 'Lembrar',
  understand: 'Entender',
  say: 'Dizer',
};

/** Lado 1 ou 2 do item. */
export function templateSide(t: CardTemplate): 1 | 2 {
  return t === 'sound' || t === 'meaning' || t === 'read' || t === 'understand' ? 1 : 2;
}

export type AnswerMode = 'romaji' | 'kana' | 'choice' | 'text' | 'self';

/** Modo de resposta de cada lado: conferido (digitado/escolhido) ou autoavaliado. */
export function answerMode(t: CardTemplate): AnswerMode {
  switch (t) {
    case 'sound':
      return 'romaji';
    case 'write':
      return 'choice';
    case 'reading':
    case 'read':
      return 'kana';
    case 'say':
      return 'text';
    default:
      return 'self';
  }
}

// ─── FSRS (5.18.3) ─────────────────────────────────────────────────────────

export type CardSchedSettings = {
  desiredRetention: number; // 0,90
  maxIntervalDays: number; // 365
  learningSteps: string; // '1m 10m'
  relearningSteps: string; // '10m'
  enableFuzz?: boolean; // padrão true
};

export const DEFAULT_SCHED: CardSchedSettings = {
  desiredRetention: 0.9,
  maxIntervalDays: 365,
  learningSteps: '1m 10m',
  relearningSteps: '10m',
};

const parseSteps = (s: string): StepUnit[] =>
  s
    .split(/[\s,]+/)
    .map((x) => x.trim())
    .filter((x) => /^\d+(\.\d+)?[mhd]$/.test(x)) as StepUnit[];

export function makeScheduler(s: CardSchedSettings = DEFAULT_SCHED) {
  return fsrs(
    generatorParameters({
      request_retention: s.desiredRetention,
      maximum_interval: s.maxIntervalDays,
      enable_fuzz: s.enableFuzz ?? true,
      enable_short_term: true,
      learning_steps: parseSteps(s.learningSteps),
      relearning_steps: parseSteps(s.relearningSteps),
    }),
  );
}

/** Estado FSRS de um cartão, como na tabela `cards`. */
export type CardSched = {
  state: CardState;
  due: Date | null;
  stability: number | null;
  difficulty: number | null;
  elapsedDays: number;
  scheduledDays: number;
  learningStep: number;
  reps: number;
  lapses: number;
  lastReview: Date | null;
};

const STATE_TO: Record<CardState, State> = {
  new: State.New,
  learning: State.Learning,
  review: State.Review,
  relearning: State.Relearning,
};
const STATE_FROM: Record<number, CardState> = {
  [State.New]: 'new',
  [State.Learning]: 'learning',
  [State.Review]: 'review',
  [State.Relearning]: 'relearning',
};

export function toFsrsCard(c: CardSched, now: Date): FsrsCard {
  return {
    due: c.due ?? now,
    stability: c.stability ?? 0,
    difficulty: c.difficulty ?? 0,
    elapsed_days: c.elapsedDays,
    scheduled_days: c.scheduledDays,
    learning_steps: c.learningStep,
    reps: c.reps,
    lapses: c.lapses,
    state: STATE_TO[c.state],
    ...(c.lastReview ? { last_review: c.lastReview } : {}),
  };
}

export function fromFsrsCard(f: FsrsCard): CardSched {
  return {
    state: STATE_FROM[f.state]!,
    due: f.due,
    stability: f.stability,
    difficulty: f.difficulty,
    elapsedDays: f.elapsed_days,
    scheduledDays: f.scheduled_days,
    learningStep: f.learning_steps,
    reps: f.reps,
    lapses: f.lapses,
    lastReview: f.last_review ?? null,
  };
}

export type RatingValue = 1 | 2 | 3 | 4;
export const RATING_LABEL: Record<RatingValue, string> = { 1: 'De novo', 2: 'Difícil', 3: 'Bom', 4: 'Fácil' };

/** Intervalo formatado: "<1 min", "10 min", "3 h", "1 dia", "3 dias", "2 meses", "1 ano". */
export function formatInterval(ms: number): string {
  const min = ms / 60_000;
  if (min < 1) return '<1 min';
  if (min < 60) return `${Math.round(min)} min`;
  const h = min / 60;
  if (h < 24) return `${Math.round(h)} h`;
  const d = Math.round(h / 24);
  if (d < 30) return d === 1 ? '1 dia' : `${d} dias`;
  if (d < 365) {
    const m = Math.round(d / 30);
    return m === 1 ? '1 mês' : `${m} meses`;
  }
  const y = Math.round(d / 365);
  return y === 1 ? '1 ano' : `${y} anos`;
}

export type IntervalPreview = { rating: RatingValue; label: string; due: string; scheduledDays: number };

/** Prévia dos 4 botões com `scheduler.repeat(card, agora)` (K19). */
export function previewIntervals(c: CardSched, now: Date, s: CardSchedSettings = DEFAULT_SCHED): IntervalPreview[] {
  const f = makeScheduler(s);
  const prev = f.repeat(toFsrsCard(c, now), now);
  return ([1, 2, 3, 4] as RatingValue[]).map((r) => {
    const item = prev[r as Grade];
    return {
      rating: r,
      label: formatInterval(item.card.due.getTime() - now.getTime()),
      due: item.card.due.toISOString(),
      scheduledDays: item.card.scheduled_days,
    };
  });
}

/** Aplica uma nota (1–4) ao cartão. K11: review + De novo → relearning, lapses + 1, volta em 10 min. */
export function applyRating(c: CardSched, rating: RatingValue, now: Date, s: CardSchedSettings = DEFAULT_SCHED): CardSched {
  const f = makeScheduler(s);
  const r = f.next(toFsrsCard(c, now), now, rating as unknown as Grade);
  return fromFsrsCard(r.card);
}
export { Rating as FsrsRating };

// ─── Difíceis (leech) ──────────────────────────────────────────────────────

/**
 * K12: suspende quando os lapsos chegam a `leech_lapses` (8); depois de reativado, a cada 4 lapsos a mais (12, 16…).
 * Só é conferido quando a resposta gerou um lapso novo.
 */
export function shouldSuspendLeech(p: {
  lapsesBefore: number;
  lapsesAfter: number;
  leechLapses: number;
  unsuspendedAtLapses: number | null;
}): boolean {
  if (p.lapsesAfter <= p.lapsesBefore) return false;
  if (p.unsuspendedAtLapses == null) return p.lapsesAfter >= p.leechLapses;
  const threshold = Math.max(p.leechLapses, p.unsuspendedAtLapses + 4);
  return p.lapsesAfter >= threshold;
}

// ─── Item aprendido, % do nível e abertura (5.18.1) ─────────────────────────

export type CardLite = {
  state: CardState;
  scheduledDays: number;
  suspendedAt?: Date | string | null;
};

/** Todos os cartões (não suspensos) do item em review com intervalo ≥ 7 dias. */
export function isItemLearned(cards: CardLite[]): boolean {
  const live = cards.filter((c) => !c.suspendedAt);
  return live.length > 0 && live.every((c) => c.state === 'review' && c.scheduledDays >= 7);
}

/** Item já visto: algum cartão (não suspenso) saiu de novo. Conta como "em estudo" até ficar aprendido. */
export function isItemStarted(cards: CardLite[]): boolean {
  return cards.some((c) => !c.suspendedAt && c.state !== 'new');
}

/** % do nível arredondado para baixo (86,5% → 86). */
export function levelPct(learned: number, total: number): number {
  if (!total) return 0;
  return Math.floor((learned * 100) / total + 1e-9);
}

export type LevelStatus = 'complete' | 'studying' | 'locked' | 'empty';

export function levelStatus(l: { total: number; learned: number; unlocked: boolean }): LevelStatus {
  if (l.total === 0) return 'empty';
  if (l.learned >= l.total) return 'complete';
  return l.unlocked ? 'studying' : 'locked';
}

export type LevelLite = {
  id: string;
  code: string;
  name: string;
  sort: number;
  unlockPct: number;
  unlocked: boolean;
  total: number;
  learned: number;
};

/** Nome curto do nível na regra de abertura: "N5", "A2", "Hiragana". */
export function levelShortName(l: { code: string; name: string }): string {
  return l.code.length <= 2 ? l.code : l.name;
}

/** "abre com 80% do N5" */
export function unlockRuleText(prev: { code: string; name: string }, pct: number): string {
  return `abre com ${pct}% do ${levelShortName(prev)}`;
}

/**
 * Níveis que devem abrir agora (K10): o anterior está aberto e chegou a `unlock_pct` (do nível que abre)
 * de itens aprendidos; nível sem itens não segura o seguinte. Devolve os ids na ordem.
 */
export function levelsToUnlock(levels: LevelLite[]): string[] {
  const sorted = [...levels].sort((a, b) => a.sort - b.sort);
  const opened: string[] = [];
  const isOpen = (l: LevelLite) => l.unlocked || opened.includes(l.id);
  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i]!;
    const prev = sorted[i - 1]!;
    if (isOpen(cur) || !isOpen(prev)) continue;
    if (prev.total === 0 || prev.learned * 100 >= cur.unlockPct * prev.total) opened.push(cur.id);
  }
  return opened;
}

// ─── Kanji antes da palavra (5.18.4) ────────────────────────────────────────

/**
 * Uma palavra espera enquanto algum kanji dela existe como item de kanji e o lado Significado ainda não está em review.
 * `kanjiMeaning`: kanji → estado do lado Significado (só os que existem em algum baralho).
 */
export function waitingKanji(noteKanji: string[], kanjiMeaning: Map<string, CardState>): string[] {
  return noteKanji.filter((k) => {
    const st = kanjiMeaning.get(k);
    return st !== undefined && st !== 'review';
  });
}

// ─── Novos por dia e ordem de entrada (5.18.4) ──────────────────────────────

export type NewCandidate = {
  noteId: string;
  levelSort: number;
  levelCode: string;
  deckSort: number;
  deckType: NoteType;
  position: number;
  waiting: boolean;
};

const isJlpt = (code: string) => /^N[1-5]$/.test(code);

/**
 * Escolhe os itens que entram hoje (lado 1). K9: 25 disponíveis com quota 10 → 10.
 * Ordem: nível, baralho, posição; no japonês N5–N1, 1 kanji : 2 palavras. Itens esperando kanji ficam de fora.
 */
export function pickNewItems(cands: NewCandidate[], quota: number): NewCandidate[] {
  if (quota <= 0) return [];
  const avail = cands.filter((c) => !c.waiting);
  const byLevel = new Map<number, NewCandidate[]>();
  for (const c of avail) {
    const arr = byLevel.get(c.levelSort) ?? [];
    arr.push(c);
    byLevel.set(c.levelSort, arr);
  }
  const ordered: NewCandidate[] = [];
  for (const sort of [...byLevel.keys()].sort((a, b) => a - b)) {
    const arr = byLevel.get(sort)!.sort((a, b) => a.deckSort - b.deckSort || a.position - b.position);
    const code = arr[0]!.levelCode;
    const kanji = arr.filter((c) => c.deckType === 'kanji');
    const vocab = arr.filter((c) => c.deckType === 'vocab_ja');
    if (isJlpt(code) && kanji.length && vocab.length) {
      const rest = arr.filter((c) => c.deckType !== 'kanji' && c.deckType !== 'vocab_ja');
      let k = 0;
      let v = 0;
      while (k < kanji.length || v < vocab.length) {
        if (k < kanji.length) ordered.push(kanji[k++]!);
        for (let j = 0; j < 2 && v < vocab.length; j++) ordered.push(vocab[v++]!);
        if (k >= kanji.length) while (v < vocab.length) ordered.push(vocab[v++]!);
      }
      ordered.push(...rest);
    } else ordered.push(...arr);
  }
  return ordered.slice(0, quota);
}

/** "+ novos" = min(new_per_day − itens introduzidos hoje, itens disponíveis). */
export function newQuota(newPerDay: number, introducedToday: number): number {
  return Math.max(0, newPerDay - introducedToday);
}

// ─── Fila (5.18.5) ─────────────────────────────────────────────────────────

export type QueueCard = {
  id: string;
  noteId: string;
  template: CardTemplate;
  state: CardState;
  due: Date | null;
  buriedUntil: ISODate | null;
  suspendedAt: Date | string | null;
  introducedOn: ISODate | null;
};

/** Lado 2 liberado: ainda novo, com `introduced_on` (dia seguinte ao lado 1 chegar a review) ≤ hoje. */
export function isReleasedSide2(c: QueueCard, today: ISODate): boolean {
  return templateSide(c.template) === 2 && c.state === 'new' && c.introducedOn != null && c.introducedOn <= today;
}

const visible = (c: QueueCard, today: ISODate) => !c.suspendedAt && !(c.buriedUntil && c.buriedUntil > today);

/**
 * Classifica os cartões do idioma para hoje.
 * - learningNow: fila 1 (learning/relearning com due ≤ agora), por due
 * - learningLater: learning/relearning que vencem mais tarde hoje (contam em "para revisar")
 * - reviews: fila 2 (review com due ≤ fim de hoje), por due
 * - released: lados 2 liberados (contam em "para revisar", entram com os novos)
 */
export function classifyDue(cards: QueueCard[], now: Date, endOfToday: Date, today: ISODate) {
  const learningNow: QueueCard[] = [];
  const learningLater: QueueCard[] = [];
  const reviews: QueueCard[] = [];
  const released: QueueCard[] = [];
  for (const c of cards) {
    if (!visible(c, today)) continue;
    if ((c.state === 'learning' || c.state === 'relearning') && c.due) {
      if (c.due <= now) learningNow.push(c);
      else if (c.due < endOfToday) learningLater.push(c);
    } else if (c.state === 'review' && c.due && c.due < endOfToday) reviews.push(c);
    else if (isReleasedSide2(c, today)) released.push(c);
  }
  const byDue = (a: QueueCard, b: QueueCard) => a.due!.getTime() - b.due!.getTime();
  learningNow.sort(byDue);
  learningLater.sort(byDue);
  reviews.sort(byDue);
  return { learningNow, learningLater, reviews, released };
}

/** Número "para revisar hoje": filas 1 e 2 e lados 2 liberados (limitado por max_reviews_per_day). */
export function dueCount(
  cls: ReturnType<typeof classifyDue>,
  maxReviewsPerDay: number,
  reviewsDoneToday: number,
): number {
  const reviewCap = Math.max(0, maxReviewsPerDay - reviewsDoneToday);
  return cls.learningNow.length + cls.learningLater.length + Math.min(cls.reviews.length, reviewCap) + cls.released.length;
}

/**
 * Monta a fila: learning vencidos, depois reviews com 1 novo a cada 4. Os novos são os lados 2 liberados
 * seguidos dos itens que entram hoje. Se nada mais houver, adianta learning que vence em até 20 min.
 */
export function buildQueue<T extends { id: string }>(p: {
  learningNow: T[];
  reviews: T[];
  news: T[];
  learnAhead?: T[];
  limit: number;
}): T[] {
  const out: T[] = [...p.learningNow];
  let n = 0;
  let r = 0;
  while (r < p.reviews.length || n < p.news.length) {
    for (let i = 0; i < 4 && r < p.reviews.length; i++) out.push(p.reviews[r++]!);
    if (n < p.news.length) out.push(p.news[n++]!);
    if (r >= p.reviews.length) while (n < p.news.length) out.push(p.news[n++]!);
  }
  if (!out.length && p.learnAhead) out.push(...p.learnAhead);
  return out.slice(0, p.limit);
}

/**
 * Irmão: ao responder um lado, o outro lado do mesmo item que vence hoje ganha `buried_until = amanhã` (K13).
 */
export function shouldBurySibling(sib: QueueCard, endOfToday: Date, today: ISODate): boolean {
  if (sib.suspendedAt || (sib.buriedUntil && sib.buriedUntil > today)) return false;
  if (sib.state !== 'new') return !!sib.due && sib.due < endOfToday;
  return isReleasedSide2(sib, today);
}

export const buryUntil = (today: ISODate) => addDays(today, 1);

// ─── Números da tela Cartões (5.18.8) ───────────────────────────────────────

/** média = Σ active_seconds ÷ Σ reviews (30 dias); sem histórico, 20 s. */
export function avgSecondsPerReview(activeSeconds: number, reviews: number): number {
  return reviews > 0 ? activeSeconds / reviews : 20;
}

/** "~3 min" (K18: 18 × 9,1 s). */
export function estimateMinutes(cards: number, avgSec: number): number {
  return Math.round((cards * avgSec) / 60);
}

/** Acertos: revisões de cartões que estavam em review com nota ≥ 2 ÷ todas essas revisões. */
export function accuracyPct(logs: { stateBefore: CardState; rating: number }[]): number | null {
  const rv = logs.filter((l) => l.stateBefore === 'review');
  if (!rv.length) return null;
  return Math.round((rv.filter((l) => l.rating >= 2).length * 100) / rv.length);
}

// ─── Distratores do lado Escrever (5.18.2) ──────────────────────────────────

export type KanaLite = {
  char: string;
  romaji: string;
  aliases?: string[];
  script?: string;
  group?: string;
  row?: string;
};

/** Pares parecidos (de preferência como distratores). */
const SIMILAR: string[][] = [
  ['ソ', 'ン', 'リ'], ['シ', 'ツ'], ['ク', 'ケ', 'タ'], ['ウ', 'ワ', 'フ'], ['ス', 'ヌ'], ['コ', 'ユ', 'ロ'],
  ['チ', 'テ'], ['ナ', 'メ'], ['ヲ', 'ヨ'], ['ハ', 'ル'], ['セ', 'サ'], ['ノ', 'メ'], ['エ', 'ニ', 'ユ'],
  ['ぬ', 'め'], ['ね', 'れ', 'わ'], ['る', 'ろ'], ['さ', 'ち', 'き'], ['は', 'ほ'], ['い', 'り'], ['こ', 'に', 'た'],
  ['あ', 'お', 'め'], ['う', 'ら'], ['シャ', 'ツャ'], ['く', 'へ'], ['す', 'む'], ['ま', 'も'],
];

function sounds(k: KanaLite): string[] {
  return [k.romaji, ...(k.aliases ?? [])].map((x) => x.toLowerCase());
}

/** Mesmo som (じ/ぢ, ず/づ, お/を): nunca vira distrator. */
export function sameSound(a: KanaLite, b: KanaLite): boolean {
  const sa = sounds(a);
  return sounds(b).some((x) => sa.includes(x));
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * 3 distratores do mesmo silabário (de preferência do mesmo grupo e parecidos), nunca com o mesmo som.
 * Determinístico pelo `seed` (id do cartão). Devolve as 4 opções embaralhadas.
 */
export function writeChoices(target: KanaLite, pool: KanaLite[], seed: string): string[] {
  const same = pool.filter(
    (k) => k.char !== target.char && (k.script ?? '') === (target.script ?? '') && !sameSound(k, target),
  );
  const sim = SIMILAR.find((g) => g.includes(target.char)) ?? [];
  const score = (k: KanaLite) =>
    (sim.includes(k.char) ? 0 : 10) + (k.group === target.group ? 0 : 5) + (hash(seed + k.char) % 1000) / 1000;
  const picked = [...same].sort((a, b) => score(a) - score(b)).slice(0, 3).map((k) => k.char);
  const opts = [target.char, ...picked];
  return opts.sort((a, b) => hash(seed + 'o' + a) - hash(seed + 'o' + b));
}

const ROW_HINT = new Set(['ぢ', 'づ', 'を', 'ヂ', 'ヅ', 'ヲ']);

/** Pergunta do lado Escrever: o romaji; em ぢ, づ e を vem com a linha ("ji · linha d"). */
export function writePrompt(k: KanaLite): string {
  return ROW_HINT.has(k.char) && k.row ? `${k.romaji} · linha ${k.row}` : k.romaji;
}

// ─── Tempo ativo da revisão (5.18.7) ────────────────────────────────────────

/** Tempo ativo de um cartão: do momento em que aparece até a nota, no máximo 60 s (K15). */
export const CARD_ACTIVE_CAP_S = 60;
export function cardActiveSeconds(elapsedMs: number): number {
  return Math.min(CARD_ACTIVE_CAP_S, Math.max(0, elapsedMs / 1000));
}

/** Minutos da study_session da revisão: round(s ÷ 60), mínimo 1 se houve resposta (K14: 7 min 40 s → 8). */
export function reviewSessionMinutes(activeSeconds: number, reviews: number): number {
  if (reviews <= 0) return 0;
  return Math.max(1, Math.round(activeSeconds / 60));
}

// ─── Desfazer (5.18.3) ─────────────────────────────────────────────────────

export type ReviewEffects = {
  buriedCardId?: string;
  buriedPrev?: string | null;
  unlockedLevelIds?: string[];
  releasedCardId?: string;
  releasedPrev?: string | null;
  suspended?: boolean;
};

/** O que desfazer uma resposta precisa mexer além de restaurar o `card_before` (K21). */
export function undoPlan(e: ReviewEffects) {
  return {
    unbury: e.buriedCardId ? { cardId: e.buriedCardId, buriedUntil: e.buriedPrev ?? null } : null,
    relock: e.unlockedLevelIds ?? [],
    unrelease: e.releasedCardId ? { cardId: e.releasedCardId, introducedOn: e.releasedPrev ?? null } : null,
  };
}

/** Idempotência (K20): resposta já gravada com o mesmo clientId. */
export function findByClientId<T extends { clientId: string | null }>(rows: T[], clientId: string): T | null {
  return rows.find((r) => r.clientId === clientId) ?? null;
}

// ─── Furigana (5.18.10) ────────────────────────────────────────────────────

export type RubySeg = { k: string; r: string | null };

/** "学[がく]生[せい]" → [{学, がく}, {生, せい}]; sem campo, a leitura vai sobre a palavra inteira. */
export function furiganaSegments(word: string, reading?: string | null, furigana?: string | null): RubySeg[] {
  if (furigana && /\[/.test(furigana)) {
    const out: RubySeg[] = [];
    const re = /([^[\]]+?)\[([^\]]*)\]|([^[\]]+)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(furigana))) {
      if (m[1] !== undefined) {
        // texto antes do kanji (kana sem leitura) fica separado do trecho com ruby
        const base = m[1];
        const kIdx = Array.from(base).findIndex((ch) => /[一-鿿々]/.test(ch));
        if (kIdx > 0) {
          const chars = Array.from(base);
          out.push({ k: chars.slice(0, kIdx).join(''), r: null });
          out.push({ k: chars.slice(kIdx).join(''), r: m[2]! });
        } else out.push({ k: base, r: m[2]! });
      } else out.push({ k: m[3]!, r: null });
    }
    return out;
  }
  if (!reading || reading === word || !/[一-鿿々]/.test(word)) return [{ k: word, r: null }];
  return [{ k: word, r: reading }];
}

/** Furigana visível? Na frente do lado Ler fica sempre escondida (K22); no resto segue o botão. */
export function showFurigana(template: CardTemplate, face: 'front' | 'back', toggle: boolean): boolean {
  if (template === 'read' && face === 'front') return false;
  return toggle;
}

/** Kana parecidos com `char` (para o "Cuidado" do verso). */
export function similarKana(char: string): string[] {
  return (SIMILAR.find((g) => g.includes(char)) ?? []).filter((c) => c !== char);
}
